import { CheckIcon, FolderIcon } from '@heroicons/react/24/outline';

/**
 * Every group a camera belongs to, as chips.
 *
 * The group-camera map page only ever told you about the group you had selected
 * — a camera already filed under three other groups looked identical to one that
 * belonged to nothing, so people re-filed cameras that were already placed. This
 * shows the whole membership, with the selected group called out inside it.
 *
 * Presentational only. `groups` is the camera's SAVED membership (the `groups`
 * array the cameras API already returns); the pending flags describe the unsaved
 * edit for the selected group so the chips agree with the card's badge before Save.
 */

export interface CameraGroupRef {
  id: string;
  name: string;
}

export interface CameraGroupBadgesProps {
  /** camera.groups — saved memberships, [{ id, name }]. */
  groups?: CameraGroupRef[];
  /** The group currently selected on the page, if any. */
  selectedGroupId?: string | null;
  /** Its name — needed to label a pending add, which has no saved row yet. */
  selectedGroupName?: string;
  /** This camera is queued to JOIN the selected group. */
  pendingAdd?: boolean;
  /** This camera is queued to LEAVE the selected group. */
  pendingRemove?: boolean;
  /** Chips shown before the rest collapse into "+N". */
  max?: number;
  /** Render the "not in any group" hint. Off for dense layouts. */
  showEmpty?: boolean;
  className?: string;
}

type Tone = 'selected' | 'add' | 'remove' | 'other';

interface Chip extends CameraGroupRef {
  tone: Tone;
}

/**
 * Saved memberships + the unsaved edit, selected group first then alphabetical.
 * Exported so a caller can count or search memberships without re-deriving them.
 */
export function cameraGroupChips({
  groups = [],
  selectedGroupId,
  selectedGroupName,
  pendingAdd = false,
  pendingRemove = false,
}: Pick<CameraGroupBadgesProps, 'groups' | 'selectedGroupId' | 'selectedGroupName' | 'pendingAdd' | 'pendingRemove'>): Chip[] {
  const saved = Array.isArray(groups) ? groups.filter((g) => g && g.id) : [];
  const chips: Chip[] = saved.map((g) => ({
    id: g.id,
    name: g.name || g.id,
    tone: g.id === selectedGroupId ? (pendingRemove ? 'remove' : 'selected') : 'other',
  }));

  // A queued add has no membership row yet, so it has to be added by hand.
  if (pendingAdd && selectedGroupId && !saved.some((g) => g.id === selectedGroupId)) {
    chips.push({ id: selectedGroupId, name: selectedGroupName || 'Selected group', tone: 'add' });
  }

  return chips.sort((a, b) => {
    const aSel = a.id === selectedGroupId ? 0 : 1;
    const bSel = b.id === selectedGroupId ? 0 : 1;
    if (aSel !== bSel) return aSel - bSel;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Tone → theme CSS var. Tinted INLINE with color-mix rather than `bg-brand/15`:
 * Tailwind v3 silently drops an opacity modifier on a colour defined as a bare
 * `var(--x)` (which every theme colour here is), so the utility compiles to
 * nothing and the chip would render as coloured text on no background. Same
 * reason StatusTag styles itself inline.
 */
const TONE_VAR: Record<Tone, string> = {
  selected: 'var(--brand)',
  add: 'var(--warning)',
  remove: 'var(--destructive)',
  other: 'var(--muted-foreground)',
};

/** `color` at `pct`% over the page — a tint that holds up in both themes. */
const mix = (color: string, pct: number) => `color-mix(in srgb, ${color} ${pct}%, transparent)`;

function toneStyle(tone: Tone) {
  const c = TONE_VAR[tone];
  return {
    backgroundColor: mix(c, tone === 'other' ? 10 : 15),
    borderColor: mix(c, tone === 'other' ? 24 : 40),
    color: c,
  };
}

export default function CameraGroupBadges({
  groups = [],
  selectedGroupId,
  selectedGroupName,
  pendingAdd = false,
  pendingRemove = false,
  max = 2,
  showEmpty = true,
  className = '',
}: CameraGroupBadgesProps) {
  const chips = cameraGroupChips({ groups, selectedGroupId, selectedGroupName, pendingAdd, pendingRemove });

  if (chips.length === 0) {
    if (!showEmpty) return null;
    return (
      <p className={`mt-1 flex items-center gap-1 text-[9px] text-muted-foreground ${className}`}>
        <FolderIcon className="h-2.5 w-2.5 shrink-0" />
        Not in any group
      </p>
    );
  }

  const shown = chips.slice(0, max);
  const hidden = chips.slice(max);
  // The chips truncate and overflow collapses, so the full membership always
  // stays reachable on hover — that is the whole point of the row.
  const fullList = chips.map((c) => (c.tone === 'add' ? `${c.name} (adding)` : c.tone === 'remove' ? `${c.name} (removing)` : c.name));

  return (
    <div
      className={`mt-1 flex flex-wrap items-center gap-1 ${className}`}
      title={`In ${chips.length} group${chips.length === 1 ? '' : 's'}: ${fullList.join(', ')}`}
    >
      {shown.map((c) => (
        <span
          key={c.id}
          className={`inline-flex max-w-[110px] items-center gap-0.5 rounded border px-1.5 py-0.5 text-[9px] ${
            c.tone === 'other' ? '' : 'font-semibold'
          } ${c.tone === 'remove' ? 'line-through' : ''}`}
          style={toneStyle(c.tone)}
        >
          {c.tone === 'selected' && <CheckIcon className="h-2.5 w-2.5 shrink-0" strokeWidth={3} />}
          {c.tone === 'add' && <span className="shrink-0">+</span>}
          {c.tone === 'remove' && <span className="shrink-0">−</span>}
          <span className="truncate">{c.name}</span>
        </span>
      ))}
      {hidden.length > 0 && (
        <span
          className="inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-medium"
          style={toneStyle('other')}
          title={hidden.map((c) => c.name).join(', ')}
        >
          +{hidden.length}
        </span>
      )}
    </div>
  );
}
